import React, { useRef } from "react";
import {
  Grid,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { PhotoUploaderStyles } from "./PhotoUploaderStyles";
import CloseIcon from "@material-ui/icons/Close";
import "./photosstyle.css";
import PhotosUploadUtil, { truncateFileName } from "./PhotosUploadUtil";
import {
  FILE_SIZE_ERROR,
  FILE_TYPE_ERROR,
  MAX_ALLOWED_PHOTO_SIZE_IN_MB,
} from "../constants/GlobalConstants";

// Extend the File type to include the additional properties
interface ExtendedFile extends File {
  imageDisplayFileName?: string;
  additionalDescription?: string;
  imageLabel?: string;
}

interface OtherPhotosProps {
  updateFiles: (files: ExtendedFile[]) => void;
  deleteFile: (file: ExtendedFile) => void;
}

export const OtherPhotos: React.FC<OtherPhotosProps> = (props) => {
  const classes = PhotoUploaderStyles();
  const [uploadMultiPhotoList, setUploadMultiPhotoList] = React.useState<
    ExtendedFile[]
  >([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const [isInvalidFile, setIsInvalidFile] = React.useState<boolean | undefined>(
    undefined
  );
  const [fileErrorMessage, setFileErrorMessage] =
    React.useState<string>(FILE_SIZE_ERROR);
  const [imagePreview, setImagePreview] = React.useState<string | undefined>(
    undefined
  );

  // Multiple files drag and drop
  const handleFileDrop = (ev: React.DragEvent<HTMLDivElement>) => {
    handleFileAddedEvent(ev, true);
  };

  const handleDragOver = (ev: React.DragEvent<HTMLDivElement>) => {
    ev.preventDefault();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileAddedEvent(e, false);
  };

  const deleteFile = (file: ExtendedFile) => {
    const updatedFiles = uploadMultiPhotoList.filter((item) => item !== file);
    setUploadMultiPhotoList(updatedFiles);
    props.deleteFile(file);
  };

  const clearError = () => {
    setIsInvalidFile(false);
  };

  const handleFileAddedEvent = (
    event:
      | React.DragEvent<HTMLDivElement>
      | React.ChangeEvent<HTMLInputElement>,
    isDragEvent: boolean
  ) => {
    event.preventDefault();
    const selectedFiles: ExtendedFile[] = uploadMultiPhotoList || [];
    const uploadSelectedFiles: ExtendedFile[] = [];

    let files: File[] = [];

    if (isDragEvent) {
      const dragEvent = event as React.DragEvent<HTMLDivElement>;
      if (
        dragEvent.dataTransfer.items &&
        dragEvent.dataTransfer.items.length > 0
      ) {
        files = Array.from(dragEvent.dataTransfer.items).map(
          (item) => item.getAsFile() as File
        );
      }
    } else {
      const changeEvent = event as React.ChangeEvent<HTMLInputElement>;
      if (changeEvent.target.files) {
        files = Array.from(changeEvent.target.files);
      }
    }

    if (files.length > 0) {
      files.forEach((file) => {
        const selectedFile = file as ExtendedFile;
        const selectedFileSize = selectedFile.size / 1024 / 1024; // in MB
        const ALLOWED_TYPES = ["image/jpeg", "image/jpg"];
        const image = new Image();

        if (
          selectedFile &&
          ALLOWED_TYPES.includes(selectedFile.type) &&
          selectedFileSize <= MAX_ALLOWED_PHOTO_SIZE_IN_MB
        ) {
          const reader = new FileReader();
          reader.onloadend = () => {
            if (reader.result && typeof reader.result === "string") {
              image.src = reader.result;
            }
            image.onload = () => {
              const width = image.width;
              const height = image.height;
              if (width < 800 || height < 600) {
                PhotosUploadUtil(height, width, image, selectedFile);
              }
              if (typeof reader.result === "string") {
                setImagePreview(reader.result);
              }
              selectedFile.imageLabel = "Other Photo";
              selectedFile.additionalDescription = "Other Photo";
              selectedFile.imageDisplayFileName = selectedFile.name;
              selectedFiles.push(selectedFile);
              uploadSelectedFiles.push(selectedFile);

              setUploadMultiPhotoList([...selectedFiles]);
              props.updateFiles([...uploadSelectedFiles]);
              if (inputRef.current) inputRef.current.value = "";
            };
          };
          reader.readAsDataURL(selectedFile);
        } else if (selectedFileSize > MAX_ALLOWED_PHOTO_SIZE_IN_MB) {
          console.log("Error: File size more than 10 MB");
          setIsInvalidFile(true);
          setFileErrorMessage(FILE_SIZE_ERROR);
        } else {
          setIsInvalidFile(true);
          setFileErrorMessage(FILE_TYPE_ERROR);
        }
      });
    }
  };

  const initialUploadPane = (
    <Grid className={classes.container} xs={12} container item>
      <Grid container alignItems="center">
        <Grid item xs={3}>
          <input
            type="file"
            id="drop_multiple_bt"
            className="fileUpload"
            style={{ display: "none" }}
            accept=".jpeg,.jpg"
            onChange={handleFileChange}
            ref={inputRef}
            multiple
          />
          <span className={classes.icon + " icon-camera"}></span>
        </Grid>
        <Grid item xs={9}>
          <label htmlFor="drop_vin_bt" className={classes.regularText}>
            Drag & drop a file here or click to select a file
          </label>
        </Grid>
      </Grid>
    </Grid>
  );

  const uploadPhotoPreviewState = (
    <Grid container>
      {uploadMultiPhotoList.map((file, index) => (
        <Grid
          container
          className={classes.multiplePhotos}
          id="drop_zone"
          style={{ marginTop: "1rem" }}
          spacing={1}
          key={index}
        >
          <Grid item xs={2} md={5}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <img
                src={URL.createObjectURL(file)}
                alt=""
                width="auto"
                className={
                  isMobile ? classes.imagePreviewMobile : classes.imagePreview
                }
                style={{ marginLeft: "1rem" }}
              />
            </div>
          </Grid>
          <Grid
            item
            xs={8}
            md={6}
            className={classes.imagePrevName}
            style={{ paddingLeft: ".5rem", paddingRight: ".5rem" }}
          >
            <Typography>{truncateFileName(file.name)}</Typography>
          </Grid>
          <Grid
            item
            xs={2}
            md={1}
            justify="center"
            alignItems="center"
            container
          >
            <Button
              color="default"
              startIcon={<CloseIcon />}
              onClick={() => deleteFile(file)}
              className={classes.rightButton}
            />
          </Grid>
        </Grid>
      ))}
    </Grid>
  );

  const errorPane = (
    <Grid
      item
      xs={12}
      style={{
        textAlign: "center",
        paddingRight: "30px",
        paddingLeft: "30px",
      }}
    >
      <Grid container>
        <Grid container item xs={11} style={{ padding: "1rem" }}>
          <Grid item xs={10} md={6} style={{ alignContent: "center" }}>
            <span
              className="icon-no-image"
              style={{
                fontSize: "45px",
                color: "#8A0200",
              }}
            ></span>
          </Grid>
          <Grid item xs={10} md={6}>
            <label className={classes.errorText}>
              {fileErrorMessage}
              <br />
              <br /> Please select another photo.
            </label>
          </Grid>
        </Grid>

        <Grid container item alignContent="center" xs={1}>
          <Button
            color="default"
            startIcon={<CloseIcon />}
            onClick={clearError}
            className={classes.rightButton}
            data-testid={"delete_back_error"}
          />
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <Grid container>
      <Grid item xs={12} className={classes.photoSelectHead}>
        <Typography className={classes.photoSelectHead}>
          Other Photos
        </Typography>
      </Grid>
      <Grid item xs={12} className={classes.vehInfoDetails}>
        <Typography className={classes.vehInfoDetails}>
          Any other photo(s) you would like to include.
        </Typography>
      </Grid>
      <Grid
        item
        container
        xs={12}
        className={classes.multiPhotos}
        id="drop_zone"
        onDrop={handleFileDrop}
        onDragOver={handleDragOver}
        onClick={() => document.getElementById("drop_multiple_bt")?.click()}
      >
        {isInvalidFile && errorPane}
        {!isInvalidFile && initialUploadPane}
      </Grid>
      <Grid item xs={12}>
        {imagePreview && uploadPhotoPreviewState}
      </Grid>
    </Grid>
  );
};

export default OtherPhotos;
